import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin, TrendingUp, TrendingDown, ArrowUpDown, Crown, AlertTriangle,
  Building2, ChevronRight, RefreshCw, Target,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { Button } from '@verlo/shared/components/ui/button';
import { reportsApi, reportsIntelApi } from '@verlo/shared/services/api';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import { useSite } from '../../../contexts/SiteContext';
import { formatMetric, formatRange } from '../../../components/reports/formatters';
import { Sparkline } from '../../../components/reports/Sparkline';
import { FreshnessBadge } from '../../../components/reports/freshness';

const ReportsSitesPage = () => {
  const navigate = useNavigate();
  const [range, setRange] = useState('wtd');
  const [ranking, setRanking] = useState(null);
  const [compare, setCompare] = useState(null);
  const [benchmark, setBenchmark] = useState(null);
  const [freshness, setFreshness] = useState(null);
  const [sortKey, setSortKey] = useState('revenue');
  const [sortDir, setSortDir] = useState('desc');
  const [benchmarkMode, setBenchmarkMode] = useState(true);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [r, c, b, f] = await Promise.all([
        reportsIntelApi.multisiteRanking(range),
        reportsIntelApi.multisiteCompare(range),
        reportsIntelApi.multisiteBenchmark(range),
        reportsApi.getFreshness(),
      ]);
      setRanking(r.data);
      setCompare(c.data);
      setBenchmark(b.data);
      setFreshness(f.data);
    } finally {
      setLoading(false);
    }
  };

  // site_id → freshness marker, so a card or row can show "data through <last sync>" (S5-2).
  const freshnessBySite = useMemo(() => {
    const map = {};
    (freshness?.sites || []).forEach((s) => { map[s.site_id] = s; });
    return map;
  }, [freshness]);

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [range]);

  const rankMetrics = ranking?.rank_metrics || [];
  const sortedRows = useMemo(() => {
    if (!ranking) return [];
    const arr = [...ranking.rows];
    arr.sort((a, b) => {
      const av = a.metrics[sortKey] ?? 0;
      const bv = b.metrics[sortKey] ?? 0;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
    return arr;
  }, [ranking, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  // Drill into a site: the header's switcher takes it, so every report follows.
  const { setSite } = useSite();
  const goToExec = (siteId) => {
    setSite(siteId);
    navigate('/back-office/reports');
  };

  const groupTotal = useMemo(() => {
    if (!ranking) return null;
    const sum = (key) => ranking.rows.reduce((acc, r) => acc + (r.metrics[key] || 0), 0);
    return {
      revenue: sum('revenue'),
      covers: sum('covers'),
      profit: sum('profit'),
    };
  }, [ranking]);

  if (loading || !ranking) {
    return (
      <ReportsLayout>
        <div className="p-10 text-center text-[12px] text-ink-faint">Loading enterprise view…</div>
      </ReportsLayout>
    );
  }

  return (
    <ReportsLayout>
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap" data-testid="sites-toolbar">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="h-9 w-[160px] bg-white border-hairline" data-testid="sites-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {['today', 'wtd', 'mtd', 'ytd'].map((r) => (
                <SelectItem key={r} value={r}>{formatRange(r)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant={benchmarkMode ? 'default' : 'outline'}
            size="sm"
            className={`h-9 gap-1.5 ${benchmarkMode ? 'bg-verlo hover:bg-verlo-hover' : ''}`}
            onClick={() => setBenchmarkMode((v) => !v)}
            data-testid="sites-benchmark-toggle"
          >
            <Target size={13} />
            Benchmark
          </Button>
          <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={loadAll} data-testid="sites-refresh">
            <RefreshCw size={13} />
            Refresh
          </Button>
        </div>
        {groupTotal && (
          <div className="flex items-center gap-4 text-[12.5px]">
            <span className="text-ink-soft uppercase tracking-wider font-semibold">Group total</span>
            <span className="font-mono-display font-bold text-ink">{formatMetric(groupTotal.revenue, 'currency')}</span>
            <span className="text-ink-faint">·</span>
            <span className="font-mono-display font-bold text-ink">{formatMetric(groupTotal.covers, 'int')} covers</span>
            <span className="text-ink-faint">·</span>
            <span className="font-mono-display font-bold text-signal-ok">{formatMetric(groupTotal.profit, 'currency')} profit</span>
          </div>
        )}
      </div>

      {/* Compare cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="sites-compare-cards">
        {(compare?.cards || []).map((card) => {
          const isBest = ranking.superlatives.revenue?.best_site_id === card.site_id;
          return (
            <button
              key={card.site_id}
              onClick={() => goToExec(card.site_id)}
              className="text-left bg-white border border-hairline rounded-lg p-5 hover:border-verlo/30  transition-all group"
              data-testid={`sites-card-${card.site_id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <MapPin size={13} className="text-ink-faint" />
                    <h3 className="text-[14px] font-bold text-ink">{card.site_name}</h3>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    {freshnessBySite[card.site_id] && <FreshnessBadge marker={freshnessBySite[card.site_id]} />}
                    {isBest && (
                      <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-signal-warn-tint text-signal-warn border border-signal-warn-tint">
                        <Crown size={9} /> Top performer
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight size={14} className="text-ink-faint group-hover:text-verlo transition-colors" />
              </div>
              <ul className="space-y-2.5">
                {card.kpis.map((k) => (
                  <li key={k.key} className="flex items-center justify-between gap-3" data-testid={`sites-card-${card.site_id}-${k.key}`}>
                    <span className="text-[12.5px] text-ink-soft font-medium">{k.label}</span>
                    <div className="flex items-center gap-3">
                      <Sparkline values={k.spark} width={70} height={20} />
                      <span className="font-mono-display text-[12px] font-bold text-ink w-20 text-right">{formatMetric(k.value, k.format)}</span>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-4 pt-3 border-t border-hairline text-[11px] text-ink-faint uppercase tracking-wider font-semibold">
                Click to drill into Executive Dashboard →
              </div>
            </button>
          );
        })}
      </div>

      {/* Ranking table */}
      <div className="bg-white border border-hairline rounded-lg overflow-hidden mb-6" data-testid="sites-ranking-table">
        <div className="px-5 py-4 border-b border-hairline flex items-center justify-between">
          <div>
            <h3 className="text-[13px] font-semibold text-ink">Site ranking</h3>
            <p className="text-[12.5px] text-ink-soft">Sort by any metric · best & worst highlighted</p>
          </div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-ink-faint">{formatRange(range)}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-surface-2">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold text-ink-soft">Site</th>
                {rankMetrics.map((m) => (
                  <th key={m.key} className="px-4 py-2.5">
                    <button
                      className={`flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                        sortKey === m.key ? 'text-verlo' : 'text-ink-soft hover:text-ink'
                      }`}
                      onClick={() => toggleSort(m.key)}
                      data-testid={`sites-sort-${m.key}`}
                    >
                      {m.label}
                      <ArrowUpDown size={10} />
                    </button>
                  </th>
                ))}
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row, idx) => {
                const benchByMetric = benchmark
                  ? Object.fromEntries(benchmark.benchmarks.map((b) => [b.metric, b.rows.find((r) => r.site_id === row.site_id)]))
                  : {};
                return (
                  <tr key={row.site_id} className="border-t border-hairline hover:bg-hover-tint" data-testid={`sites-row-${row.site_id}`}>
                    <td className="px-4 py-3 font-semibold text-ink">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-paper text-ink-soft text-[12.5px] font-bold flex items-center justify-center font-mono-display">
                          {idx + 1}
                        </span>
                        {row.site_name}
                        {freshnessBySite[row.site_id] && <FreshnessBadge marker={freshnessBySite[row.site_id]} />}
                      </div>
                    </td>
                    {rankMetrics.map((m) => {
                      const v = row.metrics[m.key];
                      const sup = ranking.superlatives[m.key];
                      const isBest = sup?.best_site_id === row.site_id;
                      const isWorst = sup?.worst_site_id === row.site_id;
                      const b = benchmarkMode ? benchByMetric[m.key] : null;
                      let flagCls = '';
                      if (b) {
                        if (b.flag === 'over')  flagCls = 'bg-signal-ok-tint text-signal-ok';
                        if (b.flag === 'under') flagCls = 'bg-signal-stop-tint text-signal-stop';
                      }
                      return (
                        <td key={m.key} className="px-4 py-3 text-right">
                          <div className={`inline-flex flex-col items-end gap-0.5 px-2 py-1 rounded ${flagCls}`}>
                            <span className="font-mono-display font-bold text-ink">{formatMetric(v, m.format)}</span>
                            {benchmarkMode && b && b.flag !== 'in_band' && (
                              <span className={`text-[12.5px] font-mono-display flex items-center gap-0.5 ${b.flag === 'over' ? 'text-signal-ok' : 'text-signal-stop'}`}>
                                {b.flag === 'over' ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
                                {b.delta_pct > 0 ? '+' : ''}{b.delta_pct.toFixed(1)}% vs avg
                              </span>
                            )}
                            {isBest && <span className="text-[11px] uppercase tracking-wider font-semibold text-signal-warn flex items-center gap-0.5"><Crown size={9}/>best</span>}
                            {isWorst && !isBest && <span className="text-[11px] uppercase tracking-wider font-semibold text-signal-stop flex items-center gap-0.5"><AlertTriangle size={9}/>worst</span>}
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => goToExec(row.site_id)}
                        className="text-[12.5px] text-verlo hover:underline font-semibold inline-flex items-center gap-0.5"
                        data-testid={`sites-drill-${row.site_id}`}
                      >
                        Drill in <ChevronRight size={11} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Benchmark detail */}
      {benchmarkMode && benchmark && (
        <div className="bg-white border border-hairline rounded-lg p-5" data-testid="sites-benchmark-detail">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-[13px] font-semibold text-ink">Benchmark vs group average</h3>
              <p className="text-[12.5px] text-ink-soft">Outlier band ±8% · green = strong, red = needs attention</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {benchmark.benchmarks.map((b) => (
              <div key={b.metric} className="border border-hairline rounded-lg p-3" data-testid={`benchmark-${b.metric}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[12.5px] font-semibold text-ink-soft">{b.label}</span>
                  <span className="text-[12.5px] text-ink-faint font-mono-display">avg {formatMetric(b.group_avg, b.format)}</span>
                </div>
                <ul className="space-y-1">
                  {b.rows.map((r) => {
                    const flagDot =
                      r.flag === 'over' ? 'bg-signal-ok-line' :
                      r.flag === 'under' ? 'bg-signal-stop-line' :
                      'bg-ink-faint';
                    return (
                      <li key={r.site_id} className="flex items-center justify-between gap-2 text-[12.5px]">
                        <span className="flex items-center gap-1.5 text-ink-soft truncate">
                          <span className={`w-1.5 h-1.5 rounded-full ${flagDot}`} />
                          {r.site_name}
                        </span>
                        <span className="font-mono-display text-ink">
                          {formatMetric(r.value, b.format)}{' '}
                          <span className={`text-[12.5px] ${r.delta_pct > 0 ? 'text-signal-ok' : r.delta_pct < 0 ? 'text-signal-stop' : 'text-ink-faint'}`}>
                            {r.delta_pct > 0 ? '+' : ''}{r.delta_pct.toFixed(1)}%
                          </span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </ReportsLayout>
  );
};

export default ReportsSitesPage;
